/**
 * Lesson Browser — Shows available lesson packs and standalone lessons.
 *
 * Fetches lesson-catalog.json, groups by program (AP HuG, IB, NGSS),
 * lets students browse and select a lesson to start.
 */

import DOMPurify from 'dompurify'
import { R2_LESSONS_BASE, TIMEOUT_FETCH_DEFAULT, TIMEOUT_FETCH_FAST } from '../config.js'

class LessonBrowser {
  /**
   * @param {HTMLElement} container - Parent panel body element
   * @param {Function} onSelectLesson - Called with lesson URL when student picks a lesson
   */
  constructor(container, onSelectLesson) {
    this.container = container
    this.onSelectLesson = onSelectLesson
    this._catalog = null
    this._expandedPack = null
    this._packData = {} // cached pack.json data keyed by pack ID
  }

  /**
   * Show the browser. Fetches catalog if not cached.
   * @param {HTMLElement} body - The panel body element to render into
   * @param {HTMLElement} nav - The nav element to hide
   */
  async show(body, nav) {
    if (nav) nav.style.display = 'none'

    body.innerHTML = '<div class="lesson-browser-loading">Loading lessons...</div>'

    try {
      if (!this._catalog) {
        this._catalog = await this._fetchCatalog()
      }
      this._render(body)
    } catch (err) {
      // SECURITY: sanitized 2026-04-18 — err.message is a caught error string
      body.innerHTML = DOMPurify.sanitize(`
        <div class="lesson-error">
          <div class="lesson-error-icon">&#9888;</div>
          <div class="lesson-error-title">Could not load lessons</div>
          <div class="lesson-error-msg">${err.message}</div>
        </div>
      `)
    }
  }

  async _fetchCatalog() {
    // Try local first (bundled), then R2
    try {
      const localResp = await fetch('/data/lessons/lesson-catalog.json', {
        signal: AbortSignal.timeout(TIMEOUT_FETCH_FAST),
      })
      if (localResp.ok) return localResp.json()
    } catch {
      /* fall through */
    }

    try {
      const r2Resp = await fetch(`${R2_LESSONS_BASE}/lesson-catalog.json`, {
        signal: AbortSignal.timeout(TIMEOUT_FETCH_DEFAULT),
      })
      if (r2Resp.ok) return r2Resp.json()
    } catch {
      /* fall through */
    }

    throw new Error('Lesson catalog not found')
  }

  async _fetchPack(packId, packUrl) {
    if (this._packData[packId]) return this._packData[packId]

    // Resolve URL: if relative, prepend local base
    let url = packUrl
    if (!url.startsWith('http')) {
      url = `/data/lessons/${packUrl}`
    }

    const resp = await fetch(url, { signal: AbortSignal.timeout(TIMEOUT_FETCH_DEFAULT) })
    if (!resp.ok) throw new Error(`Failed to fetch pack: ${resp.status}`)
    const data = await resp.json()
    this._packData[packId] = data
    return data
  }

  _render(body) {
    const catalog = this._catalog
    let html = '<div class="lesson-browser">'
    html += '<div class="lesson-browser-title">Lesson Library</div>'

    // Group packs by program
    const programs = {}
    for (const pack of catalog.packs || []) {
      const program = pack.program || 'General'
      ;(programs[program] ||= []).push(pack)
    }

    for (const [program, packs] of Object.entries(programs)) {
      html += `<div class="lesson-program-group">`
      html += `<div class="lesson-program-title">${program}</div>`

      for (const pack of packs) {
        const isExpanded = this._expandedPack === pack.id
        html += `
          <div class="lesson-pack-card ${isExpanded ? 'expanded' : ''}" data-pack-id="${pack.id}">
            <div class="lesson-pack-header" data-pack-id="${pack.id}">
              <div class="lesson-pack-info">
                <div class="lesson-pack-title">${pack.title}</div>
                <div class="lesson-pack-meta">
                  ${pack.grade_band ? `Grade ${pack.grade_band}` : ''}
                  ${pack.lesson_count ? ` &middot; ${pack.lesson_count} lessons` : ''}
                  ${pack.difficulty ? ` &middot; ${pack.difficulty}` : ''}
                  ${pack.duration_minutes ? ` &middot; ~${pack.duration_minutes} min` : ''}
                </div>
                <div class="lesson-pack-desc">${pack.description}</div>
              </div>
              <div class="lesson-pack-chevron">${isExpanded ? '&#9660;' : '&#9654;'}</div>
            </div>
            <div class="lesson-pack-lessons" id="pack-lessons-${pack.id}" style="display:${isExpanded ? 'block' : 'none'}"></div>
          </div>
        `
      }

      html += `</div>`
    }

    // Standalone lessons
    if (catalog.standalone?.length) {
      html += `<div class="lesson-program-group">`
      html += `<div class="lesson-program-title">Standalone Lessons</div>`
      for (const lesson of catalog.standalone) {
        html += `
          <button class="lesson-card" data-lesson-url="${lesson.url}">
            <div class="lesson-card-title">${lesson.title}</div>
            <div class="lesson-card-meta">
              ${lesson.grade_band ? `Grade ${lesson.grade_band}` : ''}
              ${lesson.duration_minutes ? ` &middot; ${lesson.duration_minutes} min` : ''}
              ${lesson.difficulty ? ` &middot; ${lesson.difficulty}` : ''}
            </div>
            ${lesson.description ? `<div class="lesson-card-desc">${lesson.description}</div>` : ''}
          </button>
        `
      }
      html += `</div>`
    }

    html += '</div>'
    // SECURITY: sanitized 2026-04-18 — pack/lesson titles & descriptions come from R2 catalog
    body.innerHTML = DOMPurify.sanitize(html)

    // Wire pack expansion
    body.querySelectorAll('.lesson-pack-header').forEach((header) => {
      header.addEventListener('click', () => this._togglePack(header.dataset.packId, body))
    })

    // Wire standalone lesson clicks
    body.querySelectorAll('.lesson-card[data-lesson-url]').forEach((card) => {
      card.addEventListener('click', () => {
        this.onSelectLesson?.(card.dataset.lessonUrl)
      })
    })

    // If a pack was expanded, load its lessons
    if (this._expandedPack) {
      const pack = (catalog.packs || []).find((p) => p.id === this._expandedPack)
      if (pack) this._loadPackLessons(pack, body)
    }
  }

  async _togglePack(packId, body) {
    if (this._expandedPack === packId) {
      this._expandedPack = null
      this._render(body)
      return
    }

    this._expandedPack = packId
    this._render(body)

    const pack = (this._catalog?.packs || []).find((p) => p.id === packId)
    if (pack) await this._loadPackLessons(pack, body)
  }

  async _loadPackLessons(pack, body) {
    const container = body.querySelector(`#pack-lessons-${pack.id}`)
    if (!container) return

    container.innerHTML = '<div class="lesson-browser-loading">Loading pack...</div>'

    try {
      const packData = await this._fetchPack(pack.id, pack.pack_url)
      let html = ''

      for (const unit of packData.units || []) {
        html += `<div class="lesson-unit">`
        html += `<div class="lesson-unit-title">${unit.title}</div>`

        for (const lesson of unit.lessons || []) {
          // Resolve lesson URL: relative to pack directory
          const packDir = pack.pack_url.replace(/\/[^/]*$/, '')
          const lessonUrl = `/data/lessons/${packDir}/${lesson.file}`

          html += `
            <button class="lesson-unit-item" data-lesson-url="${lessonUrl}">
              <span class="lesson-unit-item-title">${lesson.title}</span>
              ${lesson.duration_minutes ? `<span class="lesson-unit-item-meta">${lesson.duration_minutes} min</span>` : ''}
            </button>
          `
        }

        html += `</div>`
      }

      // SECURITY: sanitized 2026-04-18 — unit/lesson titles come from R2 pack data
      container.innerHTML = DOMPurify.sanitize(html)

      // Wire lesson clicks
      container.querySelectorAll('.lesson-unit-item').forEach((item) => {
        item.addEventListener('click', () => {
          this.onSelectLesson?.(item.dataset.lessonUrl)
        })
      })
    } catch (err) {
      // SECURITY: sanitized 2026-04-18 — err.message is a caught error string
      container.innerHTML = DOMPurify.sanitize(
        `<div class="lesson-error-msg">Failed to load pack: ${err.message}</div>`
      )
    }
  }
}

export default LessonBrowser
